import { Module } from "vuex";

import {
  getPlans,
  getModules,
  saveTenant,
  getTenantInfo,
  updateTenantPlan
} from "@/api.resource/api/tenant";
import { IRootState, ITenantState } from "@/types";

const tenant: Module<ITenantState, IRootState> = {
  namespaced: true,
  state: (): ITenantState => ({
    loading: false,
    plansList: null,
    plansListSuccess: false,
    plansListMsg: null,
    modulesLoading: false,
    modules: null,
    modulesSuccess: false,
    modulesMsg: null,
    saving: false,
    saveTenantSuccess: false,
    saveTenantMsg: null,
    tenantInfo: null,
    tenantInfoSuccess: false,
    tenantInfoMsg: null,
    updateTenantPlanSuccess: false,
    updateTenantPlanMsg: null,
    provisioningOperationId: null,
    provisioningProgress: 0,
    provisioningStep: "Setting up modules...",
    provisioningStatus: null,
    provisioningError: null,
    provisioningUsingPolling: false,
    isProvisioning: false
  }),
  getters: {
    loading: state => state.loading,
    plansList: state => state.plansList,
    plansListSuccess: state => state.plansListSuccess,
    plansListMsg: state => state.plansListMsg,
    modulesLoading: state => state.modulesLoading,
    modules: state => state.modules,
    modulesSuccess: state => state.modulesSuccess,
    modulesMsg: state => state.modulesMsg,
    saving: state => state.saving,
    saveTenantSuccess: state => state.saveTenantSuccess,
    saveTenantMsg: state => state.saveTenantMsg,
    tenantInfo: state => state.tenantInfo,
    tenantInfoSuccess: state => state.tenantInfoSuccess,
    tenantInfoMsg: state => state.tenantInfoMsg,
    updateTenantPlanSuccess: state => state.updateTenantPlanSuccess,
    updateTenantPlanMsg: state => state.updateTenantPlanMsg,
    provisioningOperationId: state => state.provisioningOperationId,
    provisioningProgress: state => state.provisioningProgress,
    provisioningStep: state => state.provisioningStep,
    provisioningStatus: state => state.provisioningStatus,
    provisioningError: state => state.provisioningError,
    provisioningUsingPolling: state => state.provisioningUsingPolling,
    isProvisioning: state => state.isProvisioning
  },
  mutations: {
    SET_LOADING(state, status: boolean) {
      state.loading = status;
    },
    SET_MODULES_LOADING(state, status: boolean) {
      state.modulesLoading = status;
    },
    SET_SAVING(state, status: boolean) {
      state.saving = status;
    },
    GET_PLANS_SUCCESS(state, payload) {
      state.loading = false;
      state.plansListSuccess = true;
      state.plansListMsg = payload?.Description;
      state.plansList = payload.items;
    },
    GET_PLANS_ERROR(state, payload) {
      state.loading = false;
      state.plansListSuccess = false;
      state.plansListMsg = payload;
      state.plansList = null;
    },
    RESET_GET_PLANS(state) {
      state.loading = false;
      state.plansListSuccess = false;
      state.plansListMsg = null;
      state.plansList = null;
    },
    GET_MODULES_SUCCESS(state, payload) {
      state.modulesLoading = false;
      state.modulesSuccess = true;
      state.modulesMsg = payload?.Description;
      state.modules = payload.items;
    },
    GET_MODULES_ERROR(state, payload) {
      state.modulesLoading = false;
      state.modulesSuccess = false;
      state.modulesMsg = payload;
      state.modules = null;
    },
    RESET_GET_MODULES(state) {
      state.modulesLoading = false;
      state.modulesSuccess = false;
      state.modulesMsg = null;
      state.modules = null;
    },
    SAVE_TENANT_SUCCESS(state, payload) {
      state.saving = false;
      state.saveTenantSuccess = true;
      state.saveTenantMsg = payload;
    },
    SAVE_TENANT_ERROR(state, payload) {
      state.saving = false;
      state.saveTenantSuccess = false;
      state.saveTenantMsg = payload;
    },
    RESET_SAVE_TENANT(state) {
      state.saving = false;
      state.saveTenantSuccess = false;
      state.saveTenantMsg = null;
    },
    GET_TENANT_INFO_SUCCESS(state, payload) {
      state.loading = false;
      state.tenantInfoSuccess = true;
      state.tenantInfoMsg = payload?.Description;
      state.tenantInfo = payload?.subscription_info;
    },
    GET_TENANT_INFO_ERROR(state, payload) {
      state.loading = false;
      state.tenantInfoSuccess = false;
      state.tenantInfoMsg = payload;
      state.tenantInfo = null;
    },
    RESET_GET_TENANT_INFO(state) {
      state.loading = false;
      state.tenantInfoSuccess = false;
      state.tenantInfoMsg = null;
    },
    RESET_UPDATE_TENANT_PLAN(state) {
      state.saving = false;
      state.updateTenantPlanSuccess = false;
      state.updateTenantPlanMsg = null;
    },
    UPDATE_TENANT_PLAN_SUCCESS(state, payload) {
      state.saving = false;
      state.updateTenantPlanSuccess = true;
      state.updateTenantPlanMsg = payload.Description;
      state.tenantInfo = payload.SubscriptionInfo;
    },
    UPDATE_TENANT_PLAN_ERROR(state, payload) {
      state.saving = false;
      state.updateTenantPlanSuccess = false;
      state.updateTenantPlanMsg = payload.Description;
    },
    SET_PROVISIONING_STATE(state, payload) {
      state.provisioningOperationId = payload.provisioningOperationId ?? state.provisioningOperationId;
      state.provisioningProgress = payload.provisioningProgress ?? state.provisioningProgress;
      state.provisioningStep = payload.provisioningStep ?? state.provisioningStep;
      state.provisioningStatus = payload.provisioningStatus ?? state.provisioningStatus;
      state.provisioningError = payload.provisioningError ?? state.provisioningError;
      state.provisioningUsingPolling = payload.provisioningUsingPolling ?? state.provisioningUsingPolling;
      state.isProvisioning = payload.isProvisioning ?? state.isProvisioning;
    },
    RESET_PROVISIONING(state) {
      state.provisioningOperationId = null;
      state.provisioningProgress = 0;
      state.provisioningStep = "Setting up modules...";
      state.provisioningStatus = null;
      state.provisioningError = null;
      state.provisioningUsingPolling = false;
      state.isProvisioning = false;
    }
  },
  actions: {
    async GET_PLANS({ commit }) {
      commit("RESET_GET_PLANS");
      commit("SET_LOADING", true);
      let response = await getPlans();

      if (response?.success) {
        commit("GET_PLANS_SUCCESS", response?.result?.data);
      } else {
        commit("GET_PLANS_ERROR", response?.result?.data?.Description);
      }

      commit("SET_LOADING", false);
    },
    async GET_MODULES({ commit }) {
      commit("RESET_GET_MODULES");
      commit("SET_MODULES_LOADING", true);
      let response = await getModules();

      if (response?.success) {
        commit("GET_MODULES_SUCCESS", response?.result?.data);
      } else {
        commit("GET_MODULES_ERROR", response?.result?.data?.Description);
      }

      commit("SET_MODULES_LOADING", false);
    },
    async SAVE_TENANT({ state, commit, rootState }, payload) {
      commit("RESET_SAVE_TENANT");
      commit("SET_SAVING", true);
      let response = await saveTenant(rootState.auth.MCAdminCognitoID, payload);

      const description =
        response?.result?.data?.message ??
        response?.result?.data?.description ??
        response?.result?.data?.Description ??
        response?.message;

      if (response?.success) {
        commit("SAVE_TENANT_SUCCESS", description);
      } else {
        commit("SAVE_TENANT_ERROR", description);
      }

      commit("SET_SAVING", false);

      return response?.result?.data;
    },
    async GET_TENANT_INFO({ state, commit, rootState }) {
      commit("RESET_GET_TENANT_INFO");
      commit("SET_LOADING", true);
      let response = await getTenantInfo(rootState.auth.MCAdminCognitoID);

      if (response?.success) {
        commit("GET_TENANT_INFO_SUCCESS", response?.result?.data);
      } else {
        commit("GET_TENANT_INFO_ERROR", response?.result?.data?.Description);
      }
    },
    async UPDATE_TENANT_PLAN({ state, commit, rootState }, payload) {
      commit("RESET_UPDATE_TENANT_PLAN");
      commit("SET_SAVING", true);

      let response = await updateTenantPlan(
        rootState.auth.MCAdminCognitoID,
        payload
      );

      if (response?.success) {
        commit("UPDATE_TENANT_PLAN_SUCCESS", response?.result?.data);
      } else {
        commit("UPDATE_TENANT_PLAN_ERROR", response?.result?.data);

        commit("SET_SAVING", false);
      }
    }
  },
}

export default tenant;